// Fixture do protótipo, fabricada. Sai quando `GET /captacao` existir.

import type {
  AneryChip,
  AneryColuna,
  AneryTone,
  CampoDef,
  MomentoTipo,
  Trilha,
} from '../../../shared/ui/tiposDaEsteira';

export const LISTA_CAPTACAO: string[] = [
  'data_nascimento',
  'hospital_leito',
  'endereco',
  'bairro_cep_regiao',
  'telefone',
  'telefone_cuidador',
  'falou_com',
  'liminar',
  'ja_assistido',
  'pad_anterior',
  'diagnostico',
];

export const KIT_OPERADORA: Record<string, string[]> = {
  Bradesco: [
    'processo_inicial',
    'termo_solicitacao_id',
    'programa_id',
    'prescricao',
    'formulario_avaliacao',
    'orcamento',
    'comunicado_implantacao',
  ],
  'Unimed Guarulhos': ['programa_id', 'prescricao', 'orcamento'],
  AMIL: ['guia_tiss', 'orcamento'],
  SAMI: ['guia_tiss', 'prescricao'],
  VIVEST: ['guia_tiss', 'nota_fiscal', 'orcamento'],
};

export const SUBSTATUS: Record<string, string[]> = {
  'Novo (SIAI)': ['Recebido na caixa', 'Anexo lido'],
  // "Em apuração" NÃO tem cadeia: são quatro trilhas em paralelo, em TRILHAS.
  // As etapas 13 a 18 do processo canônico, na ordem. A alçada é substatus
  // próprio porque margem abaixo da régua NÃO segue: ela escala, e o caso
  // fica visível parado esperando quem pode decidir.
  'Orçamento': [
    'Montando o orçamento no IW',
    'Consolidando cooperativa e escala',
    'Rentabilidade em análise',
    'Aguardando alçada',
    'Enviado à operadora',
  ],
  Autorizado: ['PAD e orçamento autorizados pela operadora'],
  Declinado: ['Declinado no IW, com justificativa'],
};

/**
 * O gesto humano que fecha o passo, como o desenho da captação os classifica.
 * São 46 pontos de operação e cada um tem um destes — coletar e cruzar é da
 * máquina; confirmar, enviar e decidir é de gente.
 */
export type GestoHumano = 'confirma' | 'aprova_envio' | 'decide' | 'automatico';

export const ROTULO_DO_GESTO: Record<GestoHumano, string> = {
  confirma: 'confere',
  aprova_envio: 'aprova o envio',
  decide: 'decide',
  automatico: 'automático, visível',
};

export interface PassoDaTrilha {
  /** O substatus: o que se lê na tira quando a frente está aqui. */
  rotulo: string;
  gesto: GestoHumano;
  /**
   * As chaves de `CAMPOS` que ESTE passo pede. Ausente quer dizer que o
   * catálogo não tem chave para o que ele produz — e a falta fica visível,
   * em vez de preenchida por chute.
   */
  campos?: string[];
  botao: string;
  feito: string;
  acao: string;
  responsavel: string;
}

export interface DefinicaoDeTrilha {
  /** "A · Avaliação". A letra é como a operação chama a frente. */
  rotulo: string;
  /** O mesmo, na largura de uma coluna de quadro. A letra nunca sai. */
  curto: string;
  passos: PassoDaTrilha[];
  /**
   * Índice do passo → a outra frente que ele espera. São DUAS no fluxo
   * inteiro, e é o ponto: quase tudo corre em paralelo, e serializar o que
   * não precisa é o que escondia onde o caso estava travado.
   */
  travas?: Record<number, { trilha: Trilha; precisa: number; porque: string }>;
}

/**
 * As cinco frentes, com os passos que a operação executa em cada uma.
 *
 * `documentacao_hospitalar` é a frente B — pedir e receber a PRESCRIÇÃO do
 * hospital. Não confundir com o kit da operadora: o Processo Inicial do
 * Bradesco tem sete documentos, e parte deles (formulário assinado em casa,
 * aviso de implantação) só existe DEPOIS da 1ª diária. Exigi-lo aqui trava o
 * caso por um motivo que ainda não pode ser resolvido.
 */
export const TRILHAS: Record<Trilha, DefinicaoDeTrilha> = {
  avaliacao: {
    rotulo: 'A · Avaliação',
    curto: 'A · Avaliação',
    passos: [
      {
        rotulo: 'Solicitação à avaliadora',
        gesto: 'aprova_envio',
        campos: ['hospital_leito'],
        botao: 'Enviar a solicitação à avaliadora',
        feito: 'Solicitação enviada.',
        acao: 'A solicitação já vem montada. Conferir hospital e leito antes de enviar.',
        responsavel: 'Enf. de Captação',
      },
      {
        rotulo: 'Rota do dia',
        gesto: 'decide',
        botao: 'Aceitar a rota sugerida',
        feito: 'Rota aceita.',
        acao: 'A sugestão pesa CEP, prazo, liminar, prioridade e capacidade. Quem decide é a avaliadora.',
        responsavel: 'Enf. Avaliadora',
      },
      {
        rotulo: 'Avaliação e NEAD',
        gesto: 'confirma',
        campos: ['formulario_avaliacao'],
        botao: 'Conferir avaliação e NEAD',
        feito: 'Avaliação e NEAD conferidos.',
        acao: 'Vieram pré-preenchidos do laudo e da prescrição. Conferir antes de valer.',
        responsavel: 'Enf. Avaliadora',
      },
      {
        rotulo: 'Pontuação do NEAD',
        gesto: 'confirma',
        botao: 'Conferir a pontuação',
        feito: 'Pontuação conferida.',
        acao: 'A pontuação foi recalculada e checada contra o template Bradesco.',
        responsavel: 'Enf. Avaliadora',
      },
      {
        rotulo: 'Transcrição do Word',
        gesto: 'confirma',
        botao: 'Conferir a transcrição',
        feito: 'Transcrição conferida.',
        acao: 'Avaliação feita em Word, transcrita pela leitura. Conferir o que ela entendeu.',
        responsavel: 'Enf. Avaliadora',
      },
    ],
    // A solicitação sai de imediato; a ROTA é que precisa de CEP e região.
    travas: { 1: { trilha: 'cadastro', precisa: 2, porque: 'espera CEP e região' } },
  },
  documentacao_hospitalar: {
    rotulo: 'B · Hospital',
    curto: 'B · Hospital',
    passos: [
      {
        rotulo: 'Pedido ao hospital',
        gesto: 'aprova_envio',
        botao: 'Enviar o pedido ao hospital',
        feito: 'Pedido enviado.',
        acao: 'Prescrição e solicitação de HC, ao contato certo do hospital.',
        responsavel: 'Enf. de Captação',
      },
      {
        rotulo: 'Resposta do hospital',
        gesto: 'confirma',
        campos: ['prescricao'],
        botao: 'Conferir a prescrição extraída',
        feito: 'Prescrição conferida.',
        acao: 'A resposta foi anexada e a prescrição extraída. Conferir antes de valer.',
        responsavel: 'Administrativo de Implantação',
      },
      {
        rotulo: 'Cobrança do hospital',
        gesto: 'automatico',
        botao: 'Ver a cobrança enviada',
        feito: 'Cobrança registrada.',
        acao: 'Sem resposta, o hospital é cobrado e o contato é marcado como desatualizado.',
        responsavel: 'Plataforma',
      },
    ],
  },
  cadastro: {
    rotulo: 'C · Cadastro e família',
    curto: 'C · Cadastro',
    passos: [
      {
        rotulo: 'Dupla checagem',
        gesto: 'confirma',
        campos: ['nome_beneficiario', 'numero_cartao', 'data_nascimento'],
        botao: 'Concluir a dupla checagem',
        feito: 'Dupla checagem concluída.',
        acao: 'Os dados saíram dos documentos fotografados. Dois pares de olhos antes de valer.',
        responsavel: 'Enf. de Captação',
      },
      {
        rotulo: 'Dado fabricado barrado',
        gesto: 'confirma',
        campos: ['endereco', 'bairro_cep_regiao', 'telefone'],
        botao: 'Corrigir o cadastro temporário',
        feito: 'Cadastro corrigido.',
        acao: 'O cadastro temporário trouxe dado fabricado. Fica barrado até alguém corrigir.',
        responsavel: 'Enf. de Captação',
      },
      {
        rotulo: 'Tapete Vermelho',
        gesto: 'decide',
        botao: 'Decidir sobre o Tapete Vermelho',
        feito: 'Marcação decidida.',
        acao: 'A marcação foi sugerida. Quem decide é a coordenação.',
        responsavel: 'Coordenação do Núcleo',
      },
    ],
  },
  validacao_pad: {
    rotulo: 'D · PAD',
    curto: 'D · PAD',
    passos: [
      {
        rotulo: 'PAD estruturado',
        gesto: 'confirma',
        campos: ['pdf_pad'],
        botao: 'Conferir o PAD estruturado',
        feito: 'PAD conferido.',
        acao: 'Profissionais, frequência, dispositivos e dieta. No Bradesco o PAD vem no próprio SIAI.',
        responsavel: 'Enf. de Captação',
      },
      {
        rotulo: 'PAD × avaliação',
        gesto: 'decide',
        campos: ['leitura_pedido_medico'],
        botao: 'Decidir sobre a divergência',
        feito: 'Divergência decidida.',
        acao: 'A comparação apontou divergência entre o PAD da operadora e a avaliação.',
        responsavel: 'Enf. Avaliadora',
      },
      {
        rotulo: 'Pacote ao EMAD',
        gesto: 'automatico',
        botao: 'Ver o pacote enviado',
        feito: 'Pacote registrado.',
        acao: 'O pacote do parecer foi ao EMAD e à nutrição.',
        responsavel: 'Plataforma',
      },
    ],
  },
  equipe: {
    rotulo: 'E · Equipe',
    curto: 'E · Equipe',
    passos: [
      {
        rotulo: 'Comunicados de prospecção',
        gesto: 'aprova_envio',
        campos: ['profissional'],
        botao: 'Enviar os comunicados',
        feito: 'Comunicados enviados.',
        acao: 'Um comunicado por especialidade, já gerado. Sai com clique.',
        responsavel: 'Enf. de Captação',
      },
      {
        rotulo: 'Respostas consolidadas',
        gesto: 'confirma',
        campos: ['cooperativa', 'valor_proposto', 'disponivel_em'],
        botao: 'Conferir as respostas',
        feito: 'Respostas conferidas.',
        acao: 'Valor, data e disponibilidade de cada cooperativa, já consolidados.',
        responsavel: 'Enf. de Captação com a Cooperativa',
      },
      {
        rotulo: 'Ranqueamento',
        gesto: 'decide',
        botao: 'Escolher a cooperativa',
        feito: 'Cooperativa escolhida.',
        acao: 'Ranqueadas por menor valor com margem, pesando o relacionamento.',
        responsavel: 'Coordenação do Núcleo',
      },
      {
        rotulo: 'Cobrança no corte',
        gesto: 'automatico',
        botao: 'Ver a cobrança enviada',
        feito: 'Cobrança registrada.',
        acao: 'Corte de 24h, e de hora em hora quando há liminar.',
        responsavel: 'Plataforma',
      },
    ],
    // Ranquear por margem sem o parecer é escolher o que ainda pode mudar.
    travas: { 2: { trilha: 'validacao_pad', precisa: 2, porque: 'espera o parecer do EMAD' } },
  },
};

/** Token do modelo → o nome que a operação usa. */
export const ROTULO_DA_TRILHA: Record<Trilha, string> = {
  avaliacao: TRILHAS.avaliacao.rotulo,
  documentacao_hospitalar: TRILHAS.documentacao_hospitalar.rotulo,
  cadastro: TRILHAS.cadastro.rotulo,
  validacao_pad: TRILHAS.validacao_pad.rotulo,
  equipe: TRILHAS.equipe.rotulo,
};

/** O mesmo, curto — a tira do card tem a largura de uma coluna. */
export const ROTULO_CURTO_DA_TRILHA: Record<Trilha, string> = {
  avaliacao: TRILHAS.avaliacao.curto,
  documentacao_hospitalar: TRILHAS.documentacao_hospitalar.curto,
  cadastro: TRILHAS.cadastro.curto,
  validacao_pad: TRILHAS.validacao_pad.curto,
  equipe: TRILHAS.equipe.curto,
};

export const ORDEM_DAS_TRILHAS: Trilha[] = [
  'avaliacao',
  'documentacao_hospitalar',
  'cadastro',
  'validacao_pad',
  'equipe',
];

export const MOMENTO_ESTADO: Record<
  MomentoTipo,
  { rotulo: string; tone: AneryTone; pessoa: boolean }
> = {
  recebido: { rotulo: 'Recebido', tone: 'neutral', pessoa: false },
  // Escalar não é pedir aprovação: é a mesma pendência, sem resposta, subindo
  // de mão. O rótulo tem que dizer isso, senão a fila parece cheia de decisão.
  escalado: { rotulo: 'Sem resposta · escalado', tone: 'warn', pessoa: true },
  atrasado: { rotulo: 'Fora do prazo', tone: 'block', pessoa: true },
  analise: { rotulo: 'Em análise', tone: 'neutral', pessoa: false },
  implantado: { rotulo: 'Implantado', tone: 'ok', pessoa: false },
  declinado: { rotulo: 'Declinado', tone: 'block', pessoa: false },
  preencher: { rotulo: 'Falta informação', tone: 'warn', pessoa: true },
  regra: { rotulo: 'Travado na conferência', tone: 'block', pessoa: true },
  revisao: { rotulo: 'Confira antes de seguir', tone: 'info', pessoa: true },
  envio: { rotulo: 'Pronto para enviar', tone: 'ok', pessoa: false },
  alcada: { rotulo: 'Precisa de aprovação', tone: 'block', pessoa: true },
  handoff: { rotulo: 'Passagem de plantão', tone: 'info', pessoa: true },
};

export const CAMPOS: Record<string, CampoDef> = {
  // cadastrais — o que o SIAI nunca manda
  endereco: { rotulo: 'Endereço', tipo: 'texto', dica: 'rua, número, complemento' },
  bairro_cep_regiao: { rotulo: 'Bairro · CEP · região', tipo: 'texto' },
  hospital_leito: { rotulo: 'Hospital · andar · leito', tipo: 'texto' },
  telefone_cuidador: { rotulo: 'Telefone do cuidador', tipo: 'tel' },
  telefone: { rotulo: 'Telefone', tipo: 'tel' },
  falou_com: { rotulo: 'Falou com', tipo: 'texto', dica: 'nome e parentesco' },
  data_nascimento: { rotulo: 'Data de nascimento', tipo: 'data' },
  numero_cartao: { rotulo: 'Número do cartão', tipo: 'texto' },
  nome_beneficiario: { rotulo: 'Nome do beneficiário', tipo: 'texto' },
  // clínicos
  diagnostico: { rotulo: 'Diagnóstico', tipo: 'texto', dica: 'hipótese diagnóstica ou CID' },
  pad_anterior: { rotulo: 'PAD anterior', tipo: 'texto' },
  liminar: { rotulo: 'Liminar', tipo: 'select', opcoes: ['não', 'sim'] },
  ja_assistido: { rotulo: 'Já assistido pela Anery', tipo: 'select', opcoes: ['não', 'sim'] },
  // equipe
  cooperativa: { rotulo: 'Cooperativa', tipo: 'texto' },
  profissional: { rotulo: 'Profissional', tipo: 'texto', dica: 'categoria e registro' },
  valor_proposto: { rotulo: 'Valor proposto', tipo: 'numero' },
  disponivel_em: { rotulo: 'Disponível a partir de', tipo: 'data' },
  data_avaliacao_residencia: { rotulo: 'Data da avaliação da residência', tipo: 'data' },
  // documentos assinados
  tcle_assinado: { rotulo: 'TCLE assinado pelo responsável', tipo: 'arquivo' },
  pdf_pad: { rotulo: 'PDF do PAD enviado pela operadora', tipo: 'arquivo' },
  // documentos do kit — o que a operadora exige para autorizar
  processo_inicial: { rotulo: 'Processo Inicial (carta à operadora)', tipo: 'arquivo' },
  termo_solicitacao_id: { rotulo: 'Termo de Solicitação de Internação Domiciliar, assinado', tipo: 'arquivo' },
  programa_id: { rotulo: 'Programa de Internação Domiciliar (guia oficial)', tipo: 'arquivo' },
  prescricao: { rotulo: 'Prescrição — dieta, medicamentos e materiais', tipo: 'arquivo' },
  formulario_avaliacao: { rotulo: 'Formulário de Avaliação (COREN · NEAD)', tipo: 'arquivo' },
  orcamento: { rotulo: 'Orçamento', tipo: 'arquivo' },
  comunicado_implantacao: { rotulo: 'Comunicado de Implantação, com hora', tipo: 'arquivo' },
  guia_tiss: { rotulo: 'Guia TISS', tipo: 'arquivo' },
  nota_fiscal: { rotulo: 'Nota fiscal', tipo: 'arquivo' },
  // decisões — cada uma é um campo próprio, com as opções que ela admite
  leitura_pedido_medico: {
    rotulo: 'O que a leitura entendeu do pedido médico',
    tipo: 'select',
    opcoes: [
      'Confirmar: ID · enfermagem 12h + fisioterapia 2×/semana',
      'Corrigir à mão',
      'Documento ilegível — pedir reenvio',
    ],
  },
  acao_escala: {
    rotulo: 'A escala não voltou dentro das 3h',
    tipo: 'select',
    opcoes: [
      'Cobrar a Escala de novo',
      'Subir ao Coordenador do Núcleo',
      'Acionar cooperativa alternativa',
    ],
  },
  decisao_pad_fechado: {
    rotulo: 'Decisão da Gerência sobre o PAD fechado',
    tipo: 'select',
    opcoes: ['Aceitar o PAD como veio', 'Devolver à operadora com justificativa técnica'],
  },
  decisao_margem: {
    rotulo: 'Exceção de margem — alçada da Diretoria',
    tipo: 'select',
    opcoes: ['Aprovar a exceção', 'Recusar e renegociar com a operadora'],
  },
  motivo_declinio: {
    rotulo: 'Justificativa padronizada do declínio',
    tipo: 'select',
    opcoes: [
      'Sem cuidador integral (NEAD · elegibilidade)',
      'Domicílio com risco estrutural',
      'Sem impedimento de deslocamento — cabe atendimento ambulatorial',
    ],
  },
  conferencia_handoff: {
    rotulo: 'Conferir antes de receber o caso',
    tipo: 'checklist',
    itens: [
      'Orçamento',
      'Prescrição',
      'Informação sigilosa',
      'Particularidade do IW',
      'Prorrogação de rotina',
    ],
  },
  justificativa: { rotulo: 'Justificativa', tipo: 'texto_longo' },
  pendencias_abertas: { rotulo: 'Pendências que ficam abertas', tipo: 'texto_longo' },
  observacao: { rotulo: 'Observação', tipo: 'texto' },
};

/**
 * O protótipo da esteira está DESLIGADO — a tela mostra só dado real.
 *
 * Nada foi apagado: o `CAPTACAO` inteiro, as cinco frentes, os formulários por
 * frente e a simulação que move os cartões continuam aqui e testados. O que
 * esta constante tira da TELA é o que é fabricado — os cartões semeados, os
 * chips de contagem, o aviso de varredura parcial e a linha "a fila anda
 * sozinha", que contam estados simulados.
 *
 * Por que desligar: a esteira passou a receber ticket de verdade (`TP-025`), e
 * com os dois misturados o que dança é inventado e o que fica parado é real —
 * numa demonstração, é o inverso do que se quer mostrar. Enquanto as cinco
 * frentes não existirem no banco, o protótipo serve para explicar o fluxo em
 * conversa, não para dividir a tela com o dado da operação.
 *
 * Para ligar de volta: `true`, e nada mais.
 */
// Vem do ambiente, e não de um `false` literal, por dois motivos: literal faz
// o lint chamar cada uso de "condição desnecessária", e ligar o protótipo numa
// apresentação passa a ser uma variável em vez de um commit.
export const MOSTRAR_PROTOTIPO = import.meta.env.VITE_PROTOTIPO_CAPTACAO === 'true';

export const CAPTACAO = {
  breadcrumb: 'operação / captação / SIAI',
  titulo: 'Captação — SIAI vira caso',
  subtitulo:
    'O SIAI é e-mail gerado pelo sistema do Bradesco: estruturado e parseável. A entrada sai da caixa pessoal. Todo pedido nasce com dono, SLA e a hora de chegada carimbada.',
  chips: [
    { label: 'SLA ', strong: 'AD 24h · ID 48h · VM 72h' },
    { label: 'timestamp de chegada e de cada handoff' },
    { label: 'checklist barra ativação incompleta' },
  ] as AneryChip[],
  /**
   * A varredura da caixa nem sempre termina: quando para no meio, o board
   * mostra uma fatia da carteira e nada mais. Sem dizer isso na tela, o
   * coordenador conta os cards e conclui — e conta errado. O aviso é a única
   * defesa contra uma leitura parcial virar decisão.
   */
  varreduraParcial:
    'Varredura parcial: lidos 180 de 312 pacientes Bradesco. Esta contagem está incompleta — não conclua nada dela.',
  colunas: [
    {
      nome: 'Novo (SIAI)',
      contagem: '2',
      cards: [
        {
          titulo: 'Pac. 1204',
          operadora: 'Bradesco',
          sub: 'AD · desospitalização hoje',
          meta: 'chegou 08:12 · SLA 24h',
          detalhe: {
            paradaHa: { avaliacao: '2d', documentacao_hospitalar: '4d', cadastro: '1d', validacao_pad: '2d', equipe: '' },
            temDocs: ['processo_inicial', 'programa_id', 'prescricao', 'formulario_avaliacao', 'orcamento', 'comunicado_implantacao'],
            tem: ['data_nascimento', 'hospital_leito', 'liminar', 'ja_assistido', 'pad_anterior'],
            entrada: {
              campos: ['endereco', 'telefone_cuidador', 'diagnostico', 'hospital_leito'],
              botao: 'Salvar e acionar a avaliadora',
              feito: 'Captação preenchida e avaliadora acionada.',
            },
            orcamento: {
              recursos: [
                { grupo: 'equipamento', item: 'Cama hospitalar 2 movimentos', qtd: '1 · comodato' },
                { grupo: 'equipamento', item: 'Suporte de soro', qtd: '1 · comodato' },
                { grupo: 'material', item: 'Curativo hidrocoloide', qtd: '20 / mês' },
                { grupo: 'material', item: 'Luva de procedimento', qtd: '300 / mês' },
                { grupo: 'medicação', item: 'Conforme prescrição — 5 itens', qtd: 'mensal' },
              ],
              equipe: [
                { categoria: 'Técnico de enfermagem 12h', frequencia: '1 plantão / dia', origem: 'escala interna' },
                { categoria: 'Fisioterapia motora', frequencia: '2 visitas / semana', origem: 'cooperativa' },
              ],
              margem: { percentual: 22, alcada: 'Coordenador · ≥ 15% segue sem escalar', tone: 'ok' },
            },
            modalidade: 'AD · atendimento domiciliar',
            canal: 'portal SIAI → caixa bradescosp@anery.com.br',
            solicitacao: 'solicitação 4471902 · cartão ****8821',
            pad: 'enfermagem 12h + fisioterapia 2×/semana (sugerido pela operadora)',
            dono: { nome: 'Ana', papel: 'Enf. de Captação', nucleo: 'SP-1' },
            sla: { chegou: 'hoje 08:12', prazo: 'amanhã 08:12', restante: 'restam 19h42', tone: 'ok' },
            carimbos: [
              { rotulo: 'Solicitação da operadora', quando: 'hoje 08:12' },
              { rotulo: 'Direcionamento à captação', quando: 'hoje 08:31 · +19min' },
            ],
            momento: {
              tipo: 'preencher',
              texto: 'O Bradesco manda cartão, nome, PAD e prazo. Endereço, cuidador e diagnóstico nunca vêm.',
              acao: 'Preencher a solicitação de captação e acionar a enfermeira avaliadora.',
              responsavel: 'Enf. de Captação · depois Enf. Avaliadora',
            },
          },
        },
        {
          titulo: 'SIAI sem paciente identificado',
          operadora: 'Bradesco',
          sub: 'assunto fora do padrão',
          meta: 'foi para triagem manual',
          tone: 'warn',
          detalhe: {
            paradaHa: { avaliacao: '', documentacao_hospitalar: '', cadastro: '5d', validacao_pad: '', equipe: '' },
            entrada: {
              campos: ['numero_cartao', 'nome_beneficiario'],
              botao: 'Abrir o caso no paciente',
              feito: 'Caso aberto no paciente identificado.',
            },
            modalidade: 'a definir — depende de quem é o paciente',
            canal: 'portal SIAI → caixa bradescosp@anery.com.br',
            solicitacao: 'assunto recebido: "Encaminhamento — favor verificar"',
            dono: { nome: 'Carla', papel: 'Enf. de Captação', nucleo: 'SP-2' },
            sla: { chegou: 'hoje 09:05', prazo: 'a contar da identificação', restante: 'sem paciente, sem SLA', tone: 'neutral' },
            carimbos: [
              { rotulo: 'E-mail recebido', quando: 'hoje 09:05' },
              { rotulo: 'Enviado à triagem manual', quando: 'hoje 09:07 · +2min' },
            ],
            momento: {
              tipo: 'revisao',
              // Sem cartão e sem nome não há a quem ligar o caso no IW; abrir
              // assim mesmo é como nascem os duplicados.
              texto:
                'O assunto do SIAI costuma vir como "<cartão> - <NOME> - Análise de documentos recebidos". Este veio fora desse padrão, então não dá para dizer de quem é o caso.',
              acao: 'Abrir o e-mail, achar cartão e nome, e só então abrir o caso no paciente certo.',
              responsavel: 'Enf. de Captação',
            },
          },
        },
      ],
    },
    {
      nome: 'Em apuração',
      contagem: '2',
      cards: [
        {
          titulo: 'Pac. 1210',
          operadora: 'Bradesco',
          sub: 'liminar · escala venceu',
          meta: 'prazo de 3h estourado',
          tone: 'block',
          detalhe: {
            paradaHa: { avaliacao: '3d', documentacao_hospitalar: '1d', cadastro: '', validacao_pad: '1d', equipe: '5h' },
            entrada: {
              campos: ['acao_escala'],
              botao: 'Registrar',
              feito: 'Cobrança registrada, com hora.',
            },
            orcamento: {
              recursos: [
                { grupo: 'equipamento', item: 'Ventilador pulmonar', qtd: '1 · comodato' },
                { grupo: 'equipamento', item: 'Cama hospitalar 3 movimentos', qtd: '1 · comodato' },
                { grupo: 'equipamento', item: 'Aspirador de secreção', qtd: '1 · comodato' },
                { grupo: 'gases', item: 'Concentrador de O₂ 5 L/min', qtd: '1 · contínuo' },
                { grupo: 'material', item: 'Cânula de traqueostomia nº 7', qtd: '2 / mês' },
                { grupo: 'material', item: 'Sonda de aspiração', qtd: '120 / mês' },
                { grupo: 'material', item: 'Dieta enteral 1.5 kcal/mL', qtd: '60 frascos / mês' },
                { grupo: 'medicação', item: 'Conforme prescrição — 9 itens', qtd: 'mensal' },
              ],
              equipe: [
                { categoria: 'Técnico de enfermagem 24h', frequencia: '2 plantões / dia', origem: 'escala interna' },
                { categoria: 'Enfermeiro', frequencia: '1 visita / semana', origem: 'escala interna' },
                { categoria: 'Fisioterapia respiratória', frequencia: '3 visitas / semana', origem: 'cooperativa' },
                { categoria: 'Fonoaudiologia', frequencia: '2 visitas / semana', origem: 'cooperativa' },
              ],
              margem: { percentual: 18, alcada: 'Coordenador · ≥ 15% segue sem escalar', tone: 'ok' },
            },
            modalidade: 'ID · internação domiciliar por liminar',
            canal: 'portal SIAI → caixa bradescosp@anery.com.br',
            solicitacao: 'solicitação 4471933 · cartão ****9075 · determinação judicial',
            pad: 'ID 24h · implantação integral em 24h a contar da liminar',
            dono: { nome: 'Marcos', papel: 'Enf. de Captação', nucleo: 'RJ' },
            sla: {
              chegou: 'hoje 06:10',
              prazo: 'implantação até amanhã 06:10',
              restante: 'escala venceu há 1h05',
              tone: 'block',
            },
            // Em liminar o relógio é outro: 2h avaliação, 3h escala, 8h material
            // e medicamento, 24h implantação — com cobrança de retorno da escala
            // a cada 1h. Os carimbos existem para mostrar qual etapa estourou.
            carimbos: [
              { rotulo: 'Liminar recebida', quando: 'hoje 06:10' },
              { rotulo: 'Avaliação e abordagem familiar (2h)', quando: 'hoje 07:40 · dentro do prazo' },
              { rotulo: 'Escala (3h)', quando: 'venceu hoje 09:10 · sem retorno' },
              { rotulo: 'Cobranças de retorno à escala (1 em 1h)', quando: '08:10 · 09:10 · 10:10' },
              { rotulo: 'Material e medicamento (8h)', quando: 'vence hoje 14:10' },
              { rotulo: 'Implantação final (24h)', quando: 'vence amanhã 06:10' },
            ],
            momento: {
              tipo: 'preencher',
              texto:
                'Caso com liminar: os prazos são muito mais curtos que o normal. A avaliação saiu dentro das 2h, mas a escala passou das 3h e não voltou — já foram três cobranças.',
              acao: 'Cobrar a escala agora e, se não houver profissional, subir ao coordenador antes das 8h de material e medicamento.',
              responsavel: 'Escala · Coordenador do Núcleo se não voltar na próxima hora',
            },
          },
        },
        {
          titulo: 'Pac. 1190',
          operadora: 'Bradesco',
          sub: 'TCLE pendente',
          meta: 'ativação barrada pelo checklist',
          tone: 'block',
          detalhe: {
            paradaHa: { avaliacao: '1d', documentacao_hospitalar: '6d', cadastro: '2d', validacao_pad: '', equipe: '' },
            temDocs: ['processo_inicial', 'programa_id', 'prescricao', 'formulario_avaliacao', 'orcamento'],
            entrada: {
              campos: ['tcle_assinado'],
              botao: 'Anexar',
              feito: 'TCLE anexado. Kit completo — 7 de 7.',
            },
            orcamento: {
              recursos: [
                { grupo: 'equipamento', item: 'Cama hospitalar 3 movimentos', qtd: '1 · comodato' },
                { grupo: 'equipamento', item: 'Colchão pneumático', qtd: '1 · comodato' },
                { grupo: 'equipamento', item: 'Bomba de infusão', qtd: '2 · comodato' },
                { grupo: 'material', item: 'Curativo de espuma com prata', qtd: '60 / mês' },
                { grupo: 'material', item: 'Terapia por pressão negativa', qtd: '1 · aluguel mensal' },
                { grupo: 'medicação', item: 'Conforme prescrição — 12 itens', qtd: 'mensal' },
              ],
              equipe: [
                { categoria: 'Técnico de enfermagem 24h', frequencia: '2 plantões / dia', origem: 'cooperativa' },
                { categoria: 'Enfermeiro estomaterapeuta', frequencia: '3 visitas / semana', origem: 'cooperativa' },
                { categoria: 'Nutrição', frequencia: '1 visita / quinzena', origem: 'escala interna' },
              ],
              margem: {
                percentual: 6,
                alcada: 'Diretoria Operacional + Comercial · abaixo de 7% pela régua do POP.IM.001',
                tone: 'block',
              },
            },
            modalidade: 'AD · atendimento domiciliar',
            canal: 'portal SIAI → caixa bradescosp@anery.com.br',
            solicitacao: 'solicitação 4471655 · cartão ****1902',
            dono: { nome: 'Paula', papel: 'Enf. Case', nucleo: 'BH' },
            sla: { chegou: 'há 3 dias', prazo: 'implantação prevista amanhã', restante: 'trava a implantação', tone: 'block' },
            carimbos: [
              { rotulo: 'Checklist rodou', quando: 'há 2 dias 09:00' },
              { rotulo: 'Cobrança ao núcleo BH', quando: 'há 2 dias 09:02' },
              { rotulo: 'Escalado ao coordenador', quando: 'ontem 09:02 · D+1' },
            ],
            momento: {
              tipo: 'regra',
              texto:
                'O kit do Bradesco tem 7 documentos. Chegaram 6 — falta o TCLE assinado pelo responsável.',
              acao: 'Buscar o TCLE com o núcleo BH. Enquanto faltar, o envio fica travado.',
              responsavel: 'Administrativo de Implantação · termos e processos',
            },
          },
        },
      ],
    },
    {
      nome: 'Orçamento',
      contagem: '0',
      cards: [
      ],
    },
    {
      nome: 'Autorizado',
      contagem: '1',
      cards: [
        {
          titulo: 'Pac. 1193',
          operadora: 'AMIL',
          sub: 'cooperativa sem profissional na região',
          meta: 'implantação parada · risco de declínio',
          tone: 'block',
          detalhe: {
            paradaHa: { avaliacao: '', documentacao_hospitalar: '1d', cadastro: '', validacao_pad: '3d', equipe: '2d' },
            entrada: {
              campos: ['cooperativa', 'profissional', 'valor_proposto', 'disponivel_em'],
              botao: 'Registrar proposta',
              feito: 'Proposta registrada. Aguardando a cooperativa confirmar.',
            },
            orcamento: {
              recursos: [
                { grupo: 'equipamento', item: 'Cama hospitalar 2 movimentos', qtd: '1 · comodato' },
                { grupo: 'equipamento', item: 'Oxímetro de pulso', qtd: '1 · comodato' },
                { grupo: 'material', item: 'Curativo de espuma com prata', qtd: '30 / mês' },
                { grupo: 'material', item: 'Fralda geriátrica', qtd: '120 / mês' },
                { grupo: 'medicação', item: 'Conforme prescrição — 4 itens', qtd: 'mensal' },
              ],
              equipe: [
                { categoria: 'Técnico de enfermagem 12h', frequencia: '1 plantão / dia', origem: 'cooperativa' },
                { categoria: 'Enfermeiro', frequencia: '1 visita / quinzena', origem: 'escala interna' },
                { categoria: 'Fisioterapia motora', frequencia: '2 visitas / semana', origem: 'cooperativa' },
              ],
              margem: {
                percentual: 12,
                alcada: 'Sem alçada atribuída — o POP.IM.001 pula de "≥ 15% Coordenador" para "10% a 7% Gerência"',
                tone: 'warn',
              },
            },
            modalidade: 'AD · atendimento domiciliar',
            canal: 'e-mail da analista da operadora → captacao@anery.com.br',
            solicitacao: 'autorização 88240 · 30 dias · interior de SP',
            pad: 'técnico de enfermagem 12h/dia',
            dono: { nome: 'Carla', papel: 'Enf. de Captação', nucleo: 'SP-2' },
            sla: { chegou: 'há 2 dias', prazo: 'implantação prevista amanhã', restante: 'sem equipe para a data', tone: 'block' },
            carimbos: [
              { rotulo: 'Prospecção aberta na cooperativa', quando: 'há 2 dias' },
              { rotulo: 'Cooperativa respondeu', quando: 'ontem · ninguém atende a região' },
              { rotulo: 'Segunda rodada de prospecção', quando: 'hoje 08:30 · em curso' },
            ],
            momento: {
              tipo: 'preencher',
              // O loop é conhecido e tem fim: cooperativa → operadora → declínio.
              // Deixar isso explícito evita o caso ficar girando sem ninguém
              // assumir que não vai ter equipe.
              texto:
                'A prospecção não achou quem atenda a região pelo valor da tabela. É a segunda rodada com a cooperativa.',
              acao: 'Se a cooperativa não fechar hoje, renegociar o valor com a operadora. Se ela não aceitar, declinar o caso formalmente.',
              responsavel: 'Enf. de Captação com a Cooperativa · depois Gerência com a operadora',
            },
          },
        },
      ],
    },
    {
      nome: 'Declinado',
      contagem: '0',
      cards: [
      ],
    },
  ] as AneryColuna[],
  nota: 'Hoje o SIAI cai numa caixa, alguém responde "estamos dando andamento" e o IW só registra quando a case abriu o processo — não quando a operadora mandou. Sem isso não existe SLA medido por etapa. A proposta continua saindo com humano no clique.',
};

// ─────────────────────────────────────────────────────────────────────────────
// 3 · Cases
// ─────────────────────────────────────────────────────────────────────────────
